import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiStar, FiZap, FiTarget, FiTrendingUp, FiAward } = FiIcons;

const HighlightCard = ({ icon, label, value, sub, color }) => (
  <motion.div 
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="border border-[#658d71] bg-[rgba(0,0,0,0.12)] p-4 flex flex-col gap-2 relative overflow-hidden group"
  >
    <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity">
      <SafeIcon icon={icon} size={48} color={color} />
    </div>
    <div className="flex items-center gap-2">
      <SafeIcon icon={icon} size={14} style={{ color }} />
      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#658d71]">{label}</span>
    </div>
    <div className="flex flex-col">
      <span className="text-2xl font-black uppercase tracking-tight text-white leading-none">
        {value}
      </span>
      <span className="text-[10px] font-bold text-[#a9c9b1] mt-1 uppercase tracking-widest">
        {sub}
      </span>
    </div>
  </motion.div>
);

const SeasonHighlights = ({ games, standings }) => {
  const stats = useMemo(() => {
    if (!games?.length) return null;
    const finalGames = games.filter(g => g.status?.abstractGameState === 'Final');
    
    // 1. Biggest Win
    let biggestWin = { diff: 0, score: '', opp: '', date: '' };
    finalGames.forEach(g => {
      const bosHome = g.teams?.home?.team?.id === 111;
      const bosScore = Number(bosHome ? g.teams.home.score : g.teams.away.score);
      const oppScore = Number(bosHome ? g.teams.away.score : g.teams.home.score);
      const diff = bosScore - oppScore;
      if (diff > biggestWin.diff) {
        biggestWin = {
          diff,
          score: `${bosScore}-${oppScore}`,
          opp: bosHome ? g.teams.away.team.abbreviation : g.teams.home.team.abbreviation,
          date: new Date(g.gameDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        };
      }
    });

    // 2. Best Month
    const monthMap = {};
    finalGames.forEach(g => {
      const date = new Date(g.gameDate);
      const key = date.toLocaleString('en-US', { month: 'long' });
      if (!monthMap[key]) monthMap[key] = { w: 0, total: 0 };
      const bosHome = g.teams?.home?.team?.id === 111;
      const bosScore = Number(bosHome ? g.teams.home.score : g.teams.away.score);
      const oppScore = Number(bosHome ? g.teams.away.score : g.teams.home.score);
      monthMap[key].total++;
      if (bosScore > oppScore) monthMap[key].w++;
    });

    let bestMonth = { name: '--', pct: 0 };
    Object.entries(monthMap).forEach(([name, data]) => {
      const pct = (data.w / data.total) * 100;
      if (pct > bestMonth.pct) bestMonth = { name, pct: pct.toFixed(1) };
    });

    // 3. Current Rank Status
    const sox = standings?.find(t => t.team?.id === 111);
    const rank = standings ? standings.findIndex(t => t.team?.id === 111) + 1 : '--';

    return { biggestWin, bestMonth, rank: `#${rank}`, gb: sox?.gamesBack || '--' };
  }, [games, standings]);

  if (!stats) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 px-1">
        <SafeIcon icon={FiAward} size={20} className="text-[#d8ceb3]" />
        <h3 className="text-lg sm:text-xl font-black uppercase tracking-[0.2em] text-[#d8ceb3]">
          Season Performance Highlights
        </h3>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <HighlightCard 
          icon={FiZap} 
          label="Biggest Victory" 
          value={stats.biggestWin.score}
          sub={`vs ${stats.biggestWin.opp} (${stats.biggestWin.date})`}
          color="#d8ceb3"
        />
        <HighlightCard 
          icon={FiTrendingUp} 
          label="Peak Month" 
          value={stats.bestMonth.name}
          sub={`${stats.bestMonth.pct}% Win Rate`}
          color="#a9c9b1"
        />
        <HighlightCard 
          icon={FiTarget} 
          label="Division Rank" 
          value={stats.rank}
          sub={`${stats.gb} Games Back`}
          color="#e85555"
        />
        <HighlightCard 
          icon={FiStar} 
          label="Season Status" 
          value="Contending"
          sub="AL East Race 2025"
          color="#d8ceb3"
        />
      </div>
    </div>
  );
};

export default SeasonHighlights;